/**
 * @file おんがくタブ — 音楽領域のホーム。
 *
 * @what: 習い事の枠 (And Lives の「道すじ」由来のバイオリン等の項目)・練習の記録 (分数・教本・メモ)・
 *   これまでの練習・教本の一覧を 1 画面に縦積みする。記録は StudyProgressLog
 *   (realm = music, minutes) で、教本は realm = music の教材。これまでの練習の行は
 *   「なおす」であとから直せて (写真を足すのもここ)、「消す」で消せる。
 * @why: 領域タブ化の第 2 段 (代表 2026-08-24 壁打ち)。バイオリンの家庭練習 (1 日 15 分) を
 *   1 タップ強で残せるようにする。きき分けあそび (江口式) は別 PR で本パネルに足す。
 * @failure: 描画のみ。お子さんが未選択なら案内文を出す。And Lives の「道すじ」に音楽の項目が無ければ枠ごと出さない。
 *   追加の失敗 (ネットワーク断や、realm/minutes 未対応の旧 backend への送信 = デプロイ順
 *   backend → web を破った場合の 400) は actionError の 1 行で、入力は消さない (詳細は console)。
 */

import { useRef, useState } from "react";
import { addDaysLocal, formatLogDate, todayLocal } from "../api/dates";
import type { StudyExploration } from "../api/exploration";
import {
  newProgressLogId,
  type StudyMaterialInput,
  type StudyProgressLog,
  type StudyProgressLogInput,
} from "../api/plans";
import type { ProgressLogsState } from "../hooks/useProgressLogs";
import { uploadStudyLogImage } from "../lib/studyLogImage";
import { looksLikeKikiwakeLog } from "../model/kikiwake/kikiwakeLog";
import { LogEditForm } from "./LogEditForm";
import { LogImageThumb } from "./StudyLogImage";
import { MUSIC_REALM_ID, REALM_ACTIVITY_KEYWORDS } from "../model/realms";
import { KikiwakeCard } from "./KikiwakeCard";
import { MaterialCover } from "./MaterialsPanel";
import "./study.css";

/** 練習分数の既定 (バイオリン導入期の 1 日の目安)。 */
const DEFAULT_MINUTES = 15;

/** And Lives の「道すじ」から音楽の習い事の項目 (label + detail) を拾う。 */
function musicActivities(exploration: StudyExploration | null): { label: string; detail?: string }[] {
  if (!exploration) return [];
  const keywords = REALM_ACTIVITY_KEYWORDS[MUSIC_REALM_ID] ?? [];
  const out: { label: string; detail?: string }[] = [];
  for (const cluster of exploration.clusters) {
    for (const item of cluster.items) {
      if (keywords.some((k) => item.label.includes(k))) {
        out.push(item.detail ? { label: item.label, detail: item.detail } : { label: item.label });
      }
    }
  }
  return out;
}

export function MusicPanel({
  familyId,
  learnerPersonId,
  sourceHrefBase,
  planReady,
  hasLearners,
  materials,
  exploration,
  logsState,
  onAdd,
  onRemove,
  busy,
  actionError,
}: {
  familyId: string;
  /** 選択中の学習者 (きき分けカードの導線と取得に使う)。未選択は null。 */
  learnerPersonId: string | null;
  sourceHrefBase: string;
  planReady: boolean;
  hasLearners: boolean;
  /** draft の教材 (領域 music のものだけ使う)。 */
  materials: readonly StudyMaterialInput[];
  exploration: StudyExploration | null;
  logsState: ProgressLogsState;
  onAdd: (input: StudyProgressLogInput) => Promise<StudyProgressLog | null>;
  onRemove: (logId: string) => void;
  busy: boolean;
  actionError: string | null;
}) {
  const [date, setDate] = useState(todayLocal());
  const [minutes, setMinutes] = useState(DEFAULT_MINUTES);
  const [materialId, setMaterialId] = useState("");
  const [memo, setMemo] = useState("");
  const [photo, setPhoto] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // 直している最中の記録 (1 行だけ開く)
  const [editingId, setEditingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (!planReady) {
    return hasLearners ? (
      <p className="study-muted">お子さんを選ぶと練習の記録が出ます。</p>
    ) : null;
  }

  const musicMaterials = materials.filter((m) => m.realm === MUSIC_REALM_ID && m.status !== "excluded");
  const musicMaterialIds = new Set(musicMaterials.map((m) => m.id));
  const titleById = new Map(musicMaterials.map((m) => [m.id, m.title]));
  const logs = logsState.status === "ready" ? logsState.items : [];
  // きき分けあそびのセッションは専用カードにまとめ、バイオリン練習の分数・一覧を汚さない
  // (epic_24 §4.4。1 日 2〜5 件 × 数分が混ざると「1 日 15 分」の目安が読めなくなる)
  const musicLogs = logs.filter(
    (l) =>
      (l.realm === MUSIC_REALM_ID || (l.material_id !== undefined && musicMaterialIds.has(l.material_id))) &&
      !looksLikeKikiwakeLog(l),
  );
  const activities = musicActivities(exploration);
  // 直近 7 日の合計分数 (今日を含む)。日付は YYYY-MM-DD の辞書順で比較する (Date の
  // パース差異に依存しない)
  const weekAgo = addDaysLocal(todayLocal(), -6);
  const weekMinutes = musicLogs
    .filter((l) => l.date >= weekAgo)
    .reduce((n, l) => n + (l.minutes ?? 0), 0);

  const clearPhoto = () => {
    setPhoto(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async () => {
    const input: StudyProgressLogInput = {
      id: newProgressLogId(),
      date,
      realm: MUSIC_REALM_ID,
    };
    if (minutes > 0) input.minutes = minutes;
    if (materialId) input.material_id = materialId;
    if (memo.trim()) input.memo = memo.trim();
    setUploadError(null);
    if (photo) {
      // 記録より先に画像を上げる (失敗したら記録も作らない = 参照切れを作らない)
      try {
        input.image_path = await uploadStudyLogImage(photo, familyId, input.id);
      } catch (err) {
        console.error("[study] failed to upload log image", err);
        setUploadError("写真を送れませんでした。写真なしで記録するか、時間を置いてお試しください。");
        return;
      }
    }
    const created = await onAdd(input);
    if (created) {
      setMemo("");
      clearPhoto();
    }
  };

  return (
    <>
      <KikiwakeCard familyId={familyId} personId={learnerPersonId} sourceHrefBase={sourceHrefBase} />

      {activities.length > 0 ? (
        <section className="study-section">
          <h2 className="study-section-title">
            <span aria-hidden="true">🏫</span> 習い事
          </h2>
          <ul className="study-music-activities">
            {activities.map((a) => (
              <li key={a.label}>
                <strong>{a.label}</strong>
                {a.detail ? <span className="study-muted"> — {a.detail}</span> : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section className="study-section">
        <h2 className="study-section-title">
          <span aria-hidden="true">🎻</span> 練習の記録
        </h2>
        <p className="study-section-help">
          今日の練習を分数で残します（目安は 1 日 {DEFAULT_MINUTES} 分）。教本とメモは任意です。
        </p>
        <div className="study-log-form">
          <label className="study-log-field">
            <span>日付</span>
            <input
              type="date"
              value={date}
              max={todayLocal()}
              onChange={(e) => setDate(e.target.value || todayLocal())}
            />
          </label>
          <label className="study-log-field">
            <span>分</span>
            <input
              type="number"
              min={1}
              max={600}
              value={minutes}
              aria-label="練習した分数"
              onChange={(e) => setMinutes(Math.min(600, Math.max(0, Math.floor(Number(e.target.value) || 0))))}
            />
          </label>
          <label className="study-log-field">
            <span>教本</span>
            <select value={materialId} onChange={(e) => setMaterialId(e.target.value)}>
              <option value="">（教本によらない練習）</option>
              {musicMaterials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.title}
                </option>
              ))}
            </select>
          </label>
          <label className="study-log-field study-log-field--memo">
            <span>メモ</span>
            <input
              type="text"
              placeholder="例: きらきら星の前半。弓の持ち方がさまになってきた"
              maxLength={500}
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
            />
          </label>
          <label className="study-log-photo-field">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
            />
            <span className="study-ghost-btn" role="button">
              {photo ? "写真を変える" : "写真を足す"}
            </span>
          </label>
          {photo ? (
            <span className="study-log-photo-picked">
              {photo.name}
              <button type="button" className="study-link-btn" onClick={clearPhoto}>
                外す
              </button>
            </span>
          ) : null}
          <button
            type="button"
            className="study-primary-btn"
            onClick={() => void submit()}
            disabled={busy || logsState.status !== "ready" || minutes <= 0}
          >
            記録する
          </button>
        </div>
        {actionError || uploadError ? (
          <p role="alert" className="study-error">
            {uploadError ?? actionError}
          </p>
        ) : null}
      </section>

      {musicLogs.length > 0 ? (
        <section className="study-section">
          <h2 className="study-section-title">
            <span aria-hidden="true">📈</span> これまでの練習
          </h2>
          <p className="study-section-help">直近 7 日で合計 {weekMinutes} 分。</p>
          <ul className="study-music-log-list">
            {musicLogs.slice(0, 14).map((l) =>
              editingId === l.id ? (
                <li key={l.id} className="study-music-log-editing">
                  <LogEditForm
                    log={l}
                    familyId={familyId}
                    kind="music"
                    materials={musicMaterials}
                    busy={busy}
                    onSave={onAdd}
                    onClose={() => setEditingId(null)}
                  />
                </li>
              ) : (
                <li key={l.id}>
                  <span className="study-music-log-date">{formatLogDate(l.date, false)}</span>
                  <span className="study-music-log-min">{l.minutes ? `${l.minutes} 分` : "—"}</span>
                  <span className="study-music-log-body">
                    {l.material_id ? (titleById.get(l.material_id) ?? "") : ""}
                    {l.memo ? ` ${l.memo}` : ""}
                    {l.image_path ? <LogImageThumb path={l.image_path} enc={l.image_enc} /> : null}
                  </span>
                  <span className="study-log-row-actions">
                    <button
                      type="button"
                      className="study-link-btn"
                      disabled={busy}
                      aria-label={`${formatLogDate(l.date, false)} の練習の記録をなおす`}
                      onClick={() => setEditingId(l.id)}
                    >
                      なおす
                    </button>
                    <button
                      type="button"
                      className="study-link-btn"
                      disabled={busy}
                      aria-label={`${formatLogDate(l.date, false)} の練習の記録を消す`}
                      onClick={() => onRemove(l.id)}
                    >
                      消す
                    </button>
                  </span>
                </li>
              ),
            )}
          </ul>
        </section>
      ) : null}

      {musicMaterials.length > 0 ? (
        <section className="study-section">
          <h2 className="study-section-title">
            <span aria-hidden="true">📚</span> 教本
          </h2>
          <ul className="study-music-materials">
            {musicMaterials.map((m) => (
              <li key={m.id}>
                <MaterialCover isbn={m.isbn} url={m.url} title={m.title} />
                <div>
                  <div>{m.title}</div>
                  {m.memo ? <div className="study-muted">{m.memo}</div> : null}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}
